import React, { useCallback, useEffect, useState } from "react";
import { FaCopy, FaKey, FaPlus, FaSyncAlt, FaTimes } from "react-icons/fa";
import axiosInstance from "./api/axiosInstance";
import { ResultsMobileCard, ResultsMobileList } from "./ResultsMobile";
import "../assets/css/WebsiteLeads.css";

const MODE_HELP = {
  signed:
    "For sites with a server (e.g. a Cloudflare Pages Function). The server signs each request; the secret never reaches the browser. Recommended.",
  public:
    "For sites without a server. The browser sends the key in the X-Site-Key header, so it is visible in the page; it only works from the client's own domain.",
};

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function errorMessage(err, fallback) {
  const data = err?.response?.data;
  return data?.message || data?.error || fallback;
}

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="wl-field">
      <span>{label}</span>
      <div className="wl-copy">
        <code>{value}</code>
        <button type="button" className="wl-icon-btn" onClick={copy} title="Copy" aria-label={`Copy ${label}`}>
          {copied ? "✓" : <FaCopy />}
        </button>
      </div>
    </div>
  );
}

function GenerateModal({ tenants, onClose, onCreated }) {
  const [tenantId, setTenantId] = useState("");
  const [mode, setMode] = useState("signed");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!tenantId) {
      setError("Choose a client.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await axiosInstance.post("/website-form-keys", {
        Tenant_Id: Number(tenantId),
        Mode: mode,
        Label: label.trim() || undefined,
      });
      setCreated({ key: res.data?.data, secret: res.data?.secret });
      onCreated();
    } catch (err) {
      setError(errorMessage(err, "Could not generate the key."));
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    const { key, secret } = created;
    return (
      <div className="wl-modal-backdrop" role="presentation">
        <div className="wl-modal" role="dialog" aria-modal="true" aria-labelledby="wfk-created-title">
          <div className="wl-modal-head">
            <h3 id="wfk-created-title">Key generated</h3>
          </div>
          <div className="wl-alert wl-alert--warning">
            <span>Copy the {key?.Mode === "signed" ? "secret" : "key"} now. It is not stored and cannot be shown again.</span>
          </div>
          {key?.Mode === "signed" ? (
            <>
              <CopyField label="WEBSITE_FORM_KEY_ID" value={key.Key_Id} />
              <CopyField label="WEBSITE_FORM_SECRET" value={secret} />
              <p className="wl-muted">Set both as environment variables on the site's server (encrypt the secret).</p>
            </>
          ) : (
            <>
              <CopyField label="X-Site-Key" value={secret} />
              <p className="wl-muted">
                Send it in the <code>X-Site-Key</code> header from {key?.Tenant_Domain || "the client's domain"}.
              </p>
            </>
          )}
          <div className="wl-modal-actions">
            <button type="button" className="wl-btn wl-btn--primary" onClick={onClose}>
              I have copied it
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wl-modal-backdrop" role="presentation" onClick={busy ? undefined : onClose}>
      <form className="wl-modal" role="dialog" aria-modal="true" aria-labelledby="wfk-new-title" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="wl-modal-head">
          <h3 id="wfk-new-title">Generate website form key</h3>
          <button type="button" className="wl-icon-btn" onClick={onClose} disabled={busy} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <label className="wl-field">
          <span>Client</span>
          <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} disabled={busy} required>
            <option value="">Select client</option>
            {tenants.map((t) => (
              <option key={t.Tenant_Id} value={t.Tenant_Id}>
                {t.Tenant_Name} ({t.Domain})
              </option>
            ))}
          </select>
        </label>
        <label className="wl-field">
          <span>Type</span>
          <select value={mode} onChange={(e) => setMode(e.target.value)} disabled={busy}>
            <option value="signed">Signed (server)</option>
            <option value="public">Public (browser)</option>
          </select>
          <small className="wl-help">{MODE_HELP[mode]}</small>
        </label>
        <label className="wl-field">
          <span>Label (optional)</span>
          <input type="text" maxLength={100} value={label} onChange={(e) => setLabel(e.target.value)} disabled={busy} placeholder="e.g. getwellbee.com Pages" />
        </label>
        {error ? <div className="wl-alert wl-alert--error">{error}</div> : null}
        <div className="wl-modal-actions">
          <button type="button" className="wl-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="wl-btn wl-btn--primary" disabled={busy}>
            {busy ? "Generating…" : "Generate key"}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Staff management of the per-tenant keys required by POST /public/website-leads. */
function WebsiteFormKeys() {
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/website-form-keys");
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setRows([]);
      setError(errorMessage(err, "Could not load website form keys."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
    axiosInstance
      .get("/website-form-keys/tenants")
      .then((res) => setTenants(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setTenants([]));
  }, [fetchRows]);

  const revoke = async (key) => {
    if (!window.confirm(`Revoke ${key.Key_Id}? Forms using it will stop saving leads.`)) return;
    setBusyId(key.Website_Form_Key_Id);
    setNotice(null);
    try {
      await axiosInstance.put(`/website-form-keys/${key.Website_Form_Key_Id}/revoke`);
      setNotice({ type: "success", text: `${key.Key_Id} revoked.` });
      fetchRows();
    } catch (err) {
      setNotice({ type: "error", text: errorMessage(err, "Could not revoke the key.") });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="wl-panel">
      <div className="wl-toolbar">
        <p className="wl-muted wl-keys-help">
          Every client website needs an active key to submit website leads. Only a hash is stored, so a lost secret means generating a new key.
        </p>
        <div className="wl-toolbar-right wl-keys-actions">
          <button type="button" className="wl-icon-btn" onClick={fetchRows} title="Refresh" aria-label="Refresh">
            <FaSyncAlt />
          </button>
          <button type="button" className="wl-btn wl-btn--primary wl-btn--generate" onClick={() => setShowGenerate(true)}>
            <FaPlus aria-hidden="true" /> Generate key
          </button>
        </div>
      </div>

      {notice ? (
        <div className={`wl-alert wl-alert--${notice.type}`}>
          <span>{notice.text}</span>
          <button type="button" className="wl-icon-btn" onClick={() => setNotice(null)} aria-label="Dismiss">
            <FaTimes />
          </button>
        </div>
      ) : null}
      {error ? <div className="wl-alert wl-alert--error">{error}</div> : null}

      <div className="wl-table-wrap co-desktop-table">
        <table className="wl-table">
          <thead>
            <tr>
              <th>Key ID</th>
              <th>Client</th>
              <th>Type</th>
              <th>Status</th>
              <th>Last used</th>
              <th>Created</th>
              <th className="wl-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="wl-empty">Loading…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="wl-empty">No website form keys yet.</td>
              </tr>
            ) : (
              rows.map((key) => (
                <tr key={key.Website_Form_Key_Id}>
                  <td>
                    <div className="wl-name wl-nowrap">
                      <FaKey aria-hidden="true" /> <code>{key.Key_Id}</code>
                    </div>
                    {key.Label ? <div className="wl-sub">{key.Label}</div> : null}
                  </td>
                  <td>
                    <div>{key.Tenant_Name || `#${key.Tenant_Id}`}</div>
                    {key.Tenant_Domain ? <div className="wl-sub">{key.Tenant_Domain}</div> : null}
                  </td>
                  <td>{key.Mode === "signed" ? "Signed (server)" : "Public (browser)"}</td>
                  <td>
                    <span className={`wl-badge wl-badge--${key.Status === "Active" ? "converted" : "rejected"}`}>{key.Status}</span>
                  </td>
                  <td className="wl-nowrap">{formatDateTime(key.Last_Used_On)}</td>
                  <td>
                    <div className="wl-nowrap">{formatDateTime(key.Created_On)}</div>
                    {key.Created_By_Name ? <div className="wl-sub">{key.Created_By_Name}</div> : null}
                  </td>
                  <td className="wl-actions-col">
                    {key.Status === "Active" ? (
                      <button
                        type="button"
                        className="wl-btn wl-btn--sm wl-btn--danger"
                        disabled={busyId === key.Website_Form_Key_Id}
                        onClick={() => revoke(key)}
                      >
                        Revoke
                      </button>
                    ) : (
                      <span className="wl-sub">Revoked {formatDateTime(key.Revoked_On)}</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ResultsMobileList loading={loading} emptyLabel="No website form keys yet." className="wl-mobile-results">
        {rows.map((key) => {
          const active = key.Status === "Active";
          return (
            <ResultsMobileCard
              key={key.Website_Form_Key_Id}
              title={key.Key_Id}
              subtitle={key.Label || null}
              meta={[
                { label: key.Status, tone: active ? "success" : "neutral", status: true },
                { label: key.Mode === "signed" ? "Signed" : "Public", tone: "info" },
              ]}
              fields={[
                { label: "Client", value: [key.Tenant_Name || `#${key.Tenant_Id}`, key.Tenant_Domain].filter(Boolean).join(" · ") },
                { label: "Last used", value: formatDateTime(key.Last_Used_On) },
                { label: "Created", value: [formatDateTime(key.Created_On), key.Created_By_Name].filter(Boolean).join(" · ") },
                !active ? { label: "Revoked", value: formatDateTime(key.Revoked_On) } : null,
              ].filter(Boolean)}
              actions={
                active ? (
                  <div className="co-mcard__action-grid">
                    <button
                      type="button"
                      className="co-maction co-maction--danger"
                      disabled={busyId === key.Website_Form_Key_Id}
                      onClick={() => revoke(key)}
                    >
                      <FaTimes size={13} />
                      <span>Revoke</span>
                    </button>
                  </div>
                ) : null
              }
            />
          );
        })}
      </ResultsMobileList>

      {showGenerate ? <GenerateModal tenants={tenants} onClose={() => setShowGenerate(false)} onCreated={fetchRows} /> : null}
    </div>
  );
}

export default WebsiteFormKeys;
